import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/formatters';
import { toast } from '../shared/Toast';
import { v4 as uuidv4 } from 'uuid';

const BUCKET_COLORS = ['#378ADD', '#e24b4a', '#1d9e75', 'var(--gold)', '#993556', '#533F91', '#854F0B'];

const DEFAULT_DIVISION = {
  incomeMode: 'auto', // 'auto' | 'manual'
  manualIncome: '',
  expenseMode: 'auto', // 'auto' | 'manual'
  manualMonthlyExpense: '',
  buckets: [
    { id: uuidv4(), label: 'Essenciais', percent: 50, isInvestment: false },
    { id: uuidv4(), label: 'Desejos', percent: 30, isInvestment: false },
    { id: uuidv4(), label: 'Investimentos', percent: 20, isInvestment: true },
  ],
  returnRatePct: 8,
  withdrawalRatePct: 4,
};

// Média dos gastos pagos dos últimos `mesesBack` meses (ignora meses sem NENHUM
// gasto registrado — geralmente indica falta de dado, não gasto real zero).
// Usar só o mês corrente é frágil: no início do mês, ou se as contas ainda
// estiverem pendentes, o gasto aparente é 0 e quebraria a projeção inteira.
function computeAvgMonthlyExpense(transactions, mesesBack = 3) {
  const now = new Date();
  const sums = [];
  for (let i = 0; i < mesesBack; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const sum = transactions
      .filter(t => t.type === 'expense' && t.status === 'paid' && !t.cardId && t.date.startsWith(key))
      .reduce((s, t) => s + t.value, 0);
    if (sum > 0) sums.push(sum);
  }
  if (!sums.length) return 0;
  return sums.reduce((a, b) => a + b, 0) / sums.length;
}

// Simula mês a mês (juros compostos + aporte mensal) até bater o capital-alvo.
// Limite de 720 meses (60 anos) — depois disso retorna null (não alcançável nesse horizonte).
function simulateMonthsToFire({ currentCapital, monthlyContribution, annualReturnPct, targetCapital }) {
  if (targetCapital <= 0) return 0;
  if (currentCapital >= targetCapital) return 0;
  if (monthlyContribution <= 0) return null;
  const monthlyRate = Math.pow(1 + annualReturnPct / 100, 1 / 12) - 1;
  let capital = currentCapital;
  for (let m = 1; m <= 720; m++) {
    capital = capital * (1 + monthlyRate) + monthlyContribution;
    if (capital >= targetCapital) return m;
  }
  return null;
}

export default function SalaryDivision() {
  const { state, dispatch, monthIncome } = useApp();
  const saved = state.salaryDivision;
  const [division, setDivision] = useState(saved || DEFAULT_DIVISION);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (saved) setDivision(saved);
  }, [saved]);

  const update = (patch) => { setDivision(d => ({ ...d, ...patch })); setDirty(true); };
  const setBucket = (id, patch) => update({ buckets: division.buckets.map(b => b.id === id ? { ...b, ...patch } : b) });
  const addBucket = () => update({ buckets: [...division.buckets, { id: uuidv4(), label: '', percent: 0, isInvestment: false }] });
  const removeBucket = (id) => update({ buckets: division.buckets.filter(b => b.id !== id) });
  const markInvestment = (id) => update({ buckets: division.buckets.map(b => ({ ...b, isInvestment: b.id === id })) });

  const totalPercent = division.buckets.reduce((s, b) => s + (parseFloat(b.percent) || 0), 0);
  const percentOk = Math.round(totalPercent * 100) / 100 === 100;
  const income = division.incomeMode === 'auto' ? monthIncome : (parseFloat(division.manualIncome) || 0);

  const save = () => {
    if (!percentOk) { toast.error('A soma dos percentuais precisa dar 100%.'); return; }
    if (!division.buckets.some(b => b.isInvestment)) { toast.error('Marque qual fatia é a de Investimentos.'); return; }
    if (division.buckets.some(b => !b.label.trim())) { toast.error('Dê um nome pra cada fatia.'); return; }
    dispatch({ type: 'SET_SALARY_DIVISION', payload: division });
    setDirty(false);
    toast.success('Divisão de salário salva!');
  };

  // ── Projeção de Liberdade Financeira ──────────────────────────────────────
  const investmentBucket = division.buckets.find(b => b.isInvestment);
  const monthlyContribution = investmentBucket ? income * (parseFloat(investmentBucket.percent) || 0) / 100 : 0;
  // Ativos em US$ entram convertidos pelo câmbio do dia (usdV2 guarda em dólar)
  const currentCapital = (state.investments || []).reduce((s, i) => s + (i.currency === 'USD' ? (i.current || 0) * state.usdRate : (i.current || 0)), 0);
  const avgMonthlyExpense = computeAvgMonthlyExpense(state.transactions || []);
  const monthlyExpenseUsed = division.expenseMode === 'manual'
    ? (parseFloat(division.manualMonthlyExpense) || 0)
    : avgMonthlyExpense;
  const annualExpenses = monthlyExpenseUsed * 12;
  const withdrawalRate = (parseFloat(division.withdrawalRatePct) || 4) / 100;
  const targetCapital = withdrawalRate > 0 ? annualExpenses / withdrawalRate : 0;
  const months = simulateMonthsToFire({
    currentCapital,
    monthlyContribution,
    annualReturnPct: parseFloat(division.returnRatePct) || 0,
    targetCapital,
  });
  const years = months != null ? Math.floor(months / 12) : null;
  const extraMonths = months != null ? months % 12 : null;

  return (
    <div className="page-enter">
      <div className="row" style={{ marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>Divisão de Salário</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
            Defina como quer dividir sua renda e acompanhe sua projeção de liberdade financeira
          </div>
        </div>
      </div>

      {/* Renda mensal */}
      <div className="card">
        <div className="section-title">Renda mensal</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button
            className={`btn ${division.incomeMode === 'auto' ? 'btn-primary' : 'btn-ghost'}`}
            style={{ fontSize: 13 }}
            onClick={() => update({ incomeMode: 'auto' })}
          >
            <i className="fas fa-rotate"></i> Automática (do mês)
          </button>
          <button
            className={`btn ${division.incomeMode === 'manual' ? 'btn-primary' : 'btn-ghost'}`}
            style={{ fontSize: 13 }}
            onClick={() => update({ incomeMode: 'manual' })}
          >
            <i className="fas fa-pen"></i> Digitar valor
          </button>
        </div>
        {division.incomeMode === 'auto' ? (
          <div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)' }}>{formatCurrency(monthIncome)}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Receitas já lançadas este mês</div>
          </div>
        ) : (
          <div className="form-group" style={{ maxWidth: 260 }}>
            <label className="form-label">Renda mensal (R$)</label>
            <input
              className="form-control" type="number" step="0.01" min="0"
              value={division.manualIncome}
              onChange={e => update({ manualIncome: e.target.value })}
              placeholder="0,00" autoFocus
            />
          </div>
        )}
      </div>

      {/* Fatias da divisão */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="row" style={{ marginBottom: 4 }}>
          <div className="section-title" style={{ marginBottom: 0 }}>Como dividir</div>
          <span style={{ fontSize: 13, fontWeight: 700, color: percentOk ? 'var(--green)' : 'var(--red)' }}>
            {totalPercent.toFixed(0)}% {percentOk ? '✓' : '(precisa somar 100%)'}
          </span>
        </div>

        {/* Barra de distribuição */}
        <div style={{ display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden', margin: '14px 0', background: 'var(--surface2)' }}>
          {division.buckets.map((b, i) => (
            <div key={b.id} style={{ width: `${Math.max(parseFloat(b.percent) || 0, 0)}%`, background: BUCKET_COLORS[i % BUCKET_COLORS.length] }}></div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {division.buckets.map((b, i) => (
            <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: BUCKET_COLORS[i % BUCKET_COLORS.length], flexShrink: 0 }}></span>
              <input
                className="form-control" style={{ flex: '1 1 140px', minWidth: 100 }}
                value={b.label} onChange={e => setBucket(b.id, { label: e.target.value })}
                placeholder="Nome da fatia"
              />
              <input
                className="form-control" type="number" min="0" max="100" style={{ width: 70 }}
                value={b.percent} onChange={e => setBucket(b.id, { percent: e.target.value })}
              />
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>%</span>
              <span style={{ fontSize: 13, fontWeight: 600, minWidth: 100, textAlign: 'right' }}>
                {formatCurrency(income * (parseFloat(b.percent) || 0) / 100)}
              </span>
              <button
                className={`btn btn-sm ${b.isInvestment ? 'btn-primary' : 'btn-ghost'}`}
                style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                onClick={() => markInvestment(b.id)}
                title="Marcar como a fatia de Investimento"
              >
                <i className="fas fa-piggy-bank"></i> {b.isInvestment ? 'Investimento' : 'Marcar'}
              </button>
              <button
                style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: 4 }}
                onClick={() => removeBucket(b.id)} title="Remover"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          ))}
        </div>

        <button className="btn btn-ghost btn-sm" style={{ marginTop: 14 }} onClick={addBucket}>
          <i className="fas fa-plus"></i> Adicionar fatia
        </button>

        <button className="btn btn-primary btn-full" style={{ marginTop: 16, padding: 12 }} onClick={save}>
          <i className="fas fa-check"></i> Salvar divisão
        </button>
        {dirty && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, textAlign: 'center' }}>Você tem alterações não salvas</div>}
      </div>

      {/* Projeção de Liberdade Financeira */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="section-title">
          <i className="fas fa-mountain-sun" style={{ marginRight: 6, color: 'var(--gold)' }}></i>
          Projeção de Liberdade Financeira
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
          Baseado na regra dos 4%: com esse patrimônio investido, a renda dele sozinho cobriria seus gastos atuais, sem precisar trabalhar.
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Retorno estimado (% ao ano)</label>
            <input className="form-control" type="number" step="0.1" min="0" value={division.returnRatePct} onChange={e => update({ returnRatePct: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Taxa de retirada segura (% ao ano)</label>
            <input className="form-control" type="number" step="0.1" min="0.1" value={division.withdrawalRatePct} onChange={e => update({ withdrawalRatePct: e.target.value })} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Gasto mensal usado no cálculo</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <button
              className={`btn btn-sm ${division.expenseMode === 'auto' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => update({ expenseMode: 'auto' })}
            >
              <i className="fas fa-rotate"></i> Automático (média)
            </button>
            <button
              className={`btn btn-sm ${division.expenseMode === 'manual' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => update({ expenseMode: 'manual' })}
            >
              <i className="fas fa-pen"></i> Digitar valor
            </button>
          </div>
          {division.expenseMode === 'auto' ? (
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              Média dos últimos meses com gasto lançado: <strong style={{ color: 'var(--text)' }}>{formatCurrency(avgMonthlyExpense)}</strong>/mês
              {avgMonthlyExpense === 0 && <span style={{ color: 'var(--red)' }}> — sem dado suficiente ainda, considere digitar manualmente</span>}
            </div>
          ) : (
            <input
              className="form-control" type="number" step="0.01" min="0" style={{ maxWidth: 260 }}
              value={division.manualMonthlyExpense}
              onChange={e => update({ manualMonthlyExpense: e.target.value })}
              placeholder="Gasto mensal médio (R$)"
            />
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginTop: 16, marginBottom: 16 }}>
          <div className="dash-card" style={{ padding: 14 }}>
            <div className="metric-label">Aporte mensal (fatia investimento)</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{formatCurrency(monthlyContribution)}</div>
          </div>
          <div className="dash-card" style={{ padding: 14 }}>
            <div className="metric-label">Já investido hoje</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{formatCurrency(currentCapital)}</div>
          </div>
          <div className="dash-card" style={{ padding: 14 }}>
            <div className="metric-label">Gasto anual usado no cálculo</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{formatCurrency(annualExpenses)}</div>
          </div>
          <div className="dash-card" style={{ padding: 14 }}>
            <div className="metric-label">Patrimônio-alvo</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--gold)' }}>{formatCurrency(targetCapital)}</div>
          </div>
        </div>

        <div style={{
          textAlign: 'center', padding: '18px 16px', borderRadius: 12,
          background: months === 0 && targetCapital > 0 ? 'rgba(29,158,117,0.1)' : 'var(--surface2)',
        }}>
          {!investmentBucket ? (
            <div style={{ fontSize: 14, color: 'var(--muted)' }}>Marque uma fatia como "Investimento" pra ver a projeção.</div>
          ) : targetCapital <= 0 ? (
            <div style={{ fontSize: 14, color: 'var(--muted)' }}>
              Não encontrei gastos suficientes registrados pra calcular o patrimônio-alvo. Lance mais despesas no sistema ou digite o gasto mensal manualmente acima.
            </div>
          ) : months === 0 ? (
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--green)' }}>
              🎉 Você já tem patrimônio suficiente pra liberdade financeira!
            </div>
          ) : months == null ? (
            <div style={{ fontSize: 14, color: 'var(--muted)' }}>
              No ritmo atual, não dá pra calcular (aporte mensal zerado ou muito baixo) — aumente a fatia de investimento ou o retorno estimado.
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>No ritmo atual, você atinge a liberdade financeira em</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: '4px 0' }}>
                {years} ano{years !== 1 ? 's' : ''}{extraMonths > 0 ? ` e ${extraMonths} ${extraMonths !== 1 ? 'meses' : 'mês'}` : ''}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
